/** @jsxImportSource react */
import * as React from 'react';
// test
import { createContext, useMemo, PropsWithChildren, useContext, useState, useEffect } from 'react';
import { hc } from 'hono/client';
import { useFanforgeConfigs } from '@hotpot-arcade/fanforge-core-client';
import { AppType } from '@hotpot-arcade/fanforge-api';

export type FanforgeApiClient = ReturnType<typeof hc<AppType>>;

type FanforgeApiState = {
  client: FanforgeApiClient;
  isAuthenticated: boolean;
  authenticate: (jwt: string) => void;
  _debugId: string;
};

export const FanforgeApiContext = createContext<FanforgeApiState | null>(null);
export const FanforgeApiProvider = ({ children }: PropsWithChildren<{}>): React.JSX.Element => {
  const { platformApiUrl } = useFanforgeConfigs();
  const [jwt, setJwt] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Track instances to debug Vite HMR issues
  const instanceId = useMemo(() => {
    const id = Math.random().toString(36).substr(2, 9);
    return id;
  }, []);

  useEffect(() => {
    return () => {};
  }, [instanceId]);

  // Ensure context is ready after first render
  useEffect(() => {
    if (platformApiUrl) {
      setIsReady(true);
    }
  }, [platformApiUrl, instanceId]);

  const value: FanforgeApiState = useMemo(
    () => ({
      client: hc<AppType>(
        platformApiUrl,
        jwt
          ? { init: { credentials: 'include' }, headers: () => ({ Authorization: `Bearer ${jwt}` }) }
          : { init: { credentials: 'include' } }
      ),
      isAuthenticated: !!jwt,
      authenticate: (jwt: string) => setJwt(jwt),
      _debugId: instanceId,
    }),
    [platformApiUrl, jwt, instanceId]
  );

  // Don't render children until context is ready
  if (!isReady) {
    return <> </>; // or a loading spinner
  }

  return <FanforgeApiContext.Provider value={value}>{children}</FanforgeApiContext.Provider>;
};

export const useFanforgeApi = () => {
  const context = useContext(FanforgeApiContext);

  if (context === null) {
    // Add more debugging info
    console.error('useFanforgeApi: Context is null. Debug info:', {
      FanforgeApiContext,
      currentComponent: new Error().stack?.split('\n')[2]?.trim(),
    });
    throw new Error('useFanforgeApi must be used within a FanforgeApiProvider');
  }
  return context;
};

// used to infer response types with `InferResponseType`
export const apiStub: FanforgeApiClient = hc<AppType>(''); // cannot use this to make requests, just a helper for type inference
